/** Preserve recorder wall-clock seconds; offset-bearing inputs use browser local time. */
export function toPlaybackWallTime(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/.test(value)) {
    return value.length === 16 ? `${value}:00` : value;
  }
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) throw new Error('Invalid recording time');
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

// Arithmetic on recorder wall time must not inherit the browser's timezone/DST.
export const playbackMillis = (value: string) => new Date(`${toPlaybackWallTime(value)}Z`).getTime();
export const playbackWallTimeAt = (millis: number) => new Date(millis).toISOString().slice(0, 19);

export function recorderWallTime(date: Date, timeZone?: string | null) {
  if (!timeZone) return toPlaybackWallTime(date.toISOString());
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-CA', { timeZone,
      year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',second:'2-digit',hourCycle:'h23' });
  } catch {
    return toPlaybackWallTime(date.toISOString());
  }
  const parts = formatter.formatToParts(date);
  const part = (name: string) => parts.find(item => item.type === name)!.value;
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:${part('second')}`;
}

export function playbackToken(value: string, storageType: 1 | 2) {
  const wallTime = toPlaybackWallTime(value);
  return storageType === 1 ? `${wallTime.replace(/[-:]/g, '')}Z` : wallTime.replace(/[-:T]/g, '');
}

/** Cloud recordings use UTC; recorder recordings use the recorder's wall clock. */
export function recordingToken(value: string, storageType: 1 | 2, options: {
  timeZone?: string | null; wallAnchor?: string; utcAnchor?: string;
} = {}) {
  if (storageType === 2) return playbackToken(value, 2);
  const wall = toPlaybackWallTime(value);
  let instant: number;
  if (options.utcAnchor && options.wallAnchor) {
    instant = Date.parse(options.utcAnchor) + playbackMillis(wall) - playbackMillis(options.wallAnchor);
  } else if (options.timeZone) {
    const desired = playbackMillis(wall);
    instant = desired;
    for (let i = 0; i < 3; i++) {
      const shown = recorderWallTime(new Date(instant), options.timeZone);
      const difference = desired - playbackMillis(shown);
      if (!difference) break;
      instant += difference;
    }
    if (recorderWallTime(new Date(instant), options.timeZone) !== wall) {
      throw new Error('This recording time does not exist in the location timezone. Choose another time.');
    }
  } else {
    instant = new Date(wall).getTime();
  }
  if (!Number.isFinite(instant)) throw new Error('Invalid recording time');
  return new Date(instant).toISOString().slice(0,19).replace(/[-:]/g,'') + 'Z';
}

export function playbackErrorMessage(error: any): string {
  const code = Number(typeof error === 'number' ? error : error?.iErrorNum ?? error?.oError?.code ?? error?.code);
  if (code === 1019) return 'No recording was found for this time. Try a wider time window or switch the recording source.';
  if (code === 3001 || code === 1006) return 'The recorder connection was interrupted. Retry the connection, or try another recording source.';
  if (code === 10001) return 'The camera playback settings are invalid. Check the camera’s cloud connection settings.';
  return error?.oError?.msg || error?.message || (typeof error === 'string' ? error : 'Unable to play this recording. Check that the camera is online and has a recording for this time.');
}
